import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../services/api';
import { isAdmin } from '../utils/auth';

/**
 * Hook para verificar se está dentro do horário permitido
 * 
 * @param {string} horaInicio - Hora de início permitida (formato HH:MM)
 * @param {string} horaFim - Hora de fim permitida (formato HH:MM)
 * @param {boolean} bloqueioAtivo - Se o bloqueio por horário está ativo
 */
export function useHorarioPermitido(horaInicio = '07:00', horaFim = '18:00', bloqueioAtivo = true) {
    const navigate = useNavigate();
    const location = useLocation();
    const [dentroDoHorario, setDentroDoHorario] = useState(true);
    const [verificando, setVerificando] = useState(true);

    const verificarHorario = useCallback(() => {
        // Administradores não são afetados pelo bloqueio por horário
        if (isAdmin()) {
            setDentroDoHorario(true);
            setVerificando(false);
            return;
        }

        if (!bloqueioAtivo) {
            setDentroDoHorario(true);
            setVerificando(false);
            return;
        }

        const agora = new Date();
        const horaAtual = `${String(agora.getHours()).padStart(2, '0')}:${String(agora.getMinutes()).padStart(2, '0')}`;
        
        const minutosAtual = converterHoraParaMinutos(horaAtual);
        const minutosInicio = converterHoraParaMinutos(horaInicio);
        const minutosFim = converterHoraParaMinutos(horaFim);

        let dentro = false;

        if (minutosInicio <= minutosFim) {
            // Horário normal (ex: 07:00 - 18:00)
            dentro = minutosAtual >= minutosInicio && minutosAtual <= minutosFim;
        } else {
            // Horário que cruza meia-noite (ex: 18:00 - 07:00)
            dentro = minutosAtual >= minutosInicio || minutosAtual <= minutosFim;
        }

        console.log(`[HORÁRIO] Verificação: Hora atual: ${horaAtual}, Início: ${horaInicio}, Fim: ${horaFim}, Dentro: ${dentro}`);

        setDentroDoHorario(dentro);
        setVerificando(false);

        // Se estiver fora do horário, redireciona para tela de bloqueio
        if (!dentro) {
            console.log(`[HORÁRIO] ⚠️ Fora do horário permitido! Redirecionando...`);
            navigate('/bloqueio', {
                state: {
                    motivo: 'horario_bloqueado',
                    hora_inicio: horaInicio,
                    hora_fim: horaFim,
                    hora_atual: horaAtual,
                    limparToken: false // Não limpa token, apenas bloqueia acesso
                },
                replace: true
            });
        }
    }, [horaInicio, horaFim, bloqueioAtivo, navigate]);

    useEffect(() => {
        // Não verifica se não tiver token (usuário não está logado)
        if (!localStorage.getItem('token')) {
            setDentroDoHorario(true);
            setVerificando(false);
            return;
        }

        // Não verifica em páginas excluídas
        const paginasExcluidas = ['/login', '/bloqueio', '/consulta'];
        if (paginasExcluidas.includes(location.pathname)) {
            setDentroDoHorario(true);
            setVerificando(false);
            return;
        }

        console.log(`[HORÁRIO] Iniciando verificação. Início: ${horaInicio}, Fim: ${horaFim}, Ativo: ${bloqueioAtivo}`);
        verificarHorario();

        // Verifica a cada 15 segundos se ainda está dentro do horário (muito frequente para pegar mudanças do admin)
        const interval = setInterval(verificarHorario, 15000); // 15 segundos

        return () => clearInterval(interval);
    }, [verificarHorario, location.pathname, horaInicio, horaFim, bloqueioAtivo]);

    return { dentroDoHorario, verificando };
}

/**
 * Converte hora no formato HH:MM para minutos desde meia-noite
 */
function converterHoraParaMinutos(hora) {
    const [h, m] = hora.split(':').map(Number);
    return h * 60 + m;
}
